/**
 * SchedulePlanner — Planification hebdomadaire ON/OFF par WattelEM
 * V1 : stockage localStorage uniquement (visuel / démo)
 *
 * Exporte :
 *  - useDeviceSchedule(deviceSn) : hook lecture/écriture
 *  - SchedulePlannerModal       : modal plein écran d'édition
 *  - ScheduleStatusCard         : card "Prochain événement" à afficher sous Contrôle Relay
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { wattelEMApi } from '../../api/wattelEM';
import { useI18n, translate } from '../../i18n';

type TFn = ReturnType<typeof useI18n>['t'];

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TimeSlot {
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
}

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface WeeklySchedule {
  mon: TimeSlot[];
  tue: TimeSlot[];
  wed: TimeSlot[];
  thu: TimeSlot[];
  fri: TimeSlot[];
  sat: TimeSlot[];
  sun: TimeSlot[];
}

export interface DeviceSchedule {
  enabled: boolean;
  name: string;
  weekly: WeeklySchedule;
  /** Overrides ponctuels par date (ISO YYYY-MM-DD). Priorité sur le hebdo. */
  overrides?: DateOverride[];
  updatedAt: string;
}

export type DateOverrideMode = 'all-off' | 'all-on' | 'custom';

export interface DateOverride {
  date: string;          // "YYYY-MM-DD"
  mode: DateOverrideMode;
  slots?: TimeSlot[];    // si mode='custom' : plages OFF (coupures) — le reste de la journée est ON
  note?: string;         // ex: "Férié", "Pont", "Inventaire"
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers date / slots
// ─────────────────────────────────────────────────────────────────────────────

export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Retourne les plages **ON** applicables à une date donnée (override > hebdo).
 *
 * IMPORTANT — Sémantique « par défaut ON, plages stockées = COUPURES (OFF) » :
 *  - Le WattelEM est ON nativement. On stocke des plages OFF (restrictions).
 *  - Une journée SANS plage stockée (hebdo vide ET pas d'override) ⇒ ON 24h
 *  - Override "all-off" ⇒ aucune plage ON (jour entièrement coupé)
 *  - Override "all-on"  ⇒ ON 24h
 *  - Override "custom"  ⇒ slots = COUPURES, ON = complément
 *  - Hebdo avec plages  ⇒ slots = COUPURES, ON = complément
 *
 * Cette fonction renvoie donc TOUJOURS les intervalles ON (pour que le reste
 * du code — computeNextEvent, mini-bars, calendrier — reste inchangé).
 */
export function getDaySlots(schedule: DeviceSchedule, date: Date): TimeSlot[] {
  const ALL_DAY: TimeSlot[] = [{ start: '00:00', end: '23:59' }];
  const NONE: TimeSlot[] = [];
  const key = dateKey(date);
  const ov = schedule.overrides?.find(o => o.date === key);

  let storedOff: TimeSlot[];
  if (ov) {
    if (ov.mode === 'all-off') return NONE;
    if (ov.mode === 'all-on') return ALL_DAY;
    storedOff = ov.slots || [];
  } else {
    const dk = DAY_ORDER[date.getDay()];
    storedOff = schedule.weekly[dk] || [];
  }

  if (storedOff.length === 0) return ALL_DAY;
  return invertOffToOn(storedOff);
}

/** Convertit une liste de plages OFF (coupures) en plages ON (complément sur 24h). */
function invertOffToOn(offSlots: TimeSlot[]): TimeSlot[] {
  const merged: { s: number; e: number }[] = [];
  const sorted = offSlots
    .map(x => ({ s: timeToMin(x.start), e: timeToMin(x.end) }))
    .filter(x => x.e > x.s)
    .sort((a, b) => a.s - b.s);
  for (const seg of sorted) {
    const last = merged[merged.length - 1];
    if (last && seg.s <= last.e) last.e = Math.max(last.e, seg.e);
    else merged.push({ ...seg });
  }
  const on: TimeSlot[] = [];
  let cursor = 0;
  for (const m of merged) {
    if (m.s > cursor) on.push({ start: minToTime(cursor), end: minToTime(m.s) });
    cursor = Math.max(cursor, m.e);
  }
  if (cursor < 24 * 60 - 1) on.push({ start: minToTime(cursor), end: '23:59' });
  return on;
}

const DAYS: { key: DayKey; label: string; short: string }[] = [
  { key: 'mon', label: 'Lundi',    short: 'Lun' },
  { key: 'tue', label: 'Mardi',    short: 'Mar' },
  { key: 'wed', label: 'Mercredi', short: 'Mer' },
  { key: 'thu', label: 'Jeudi',    short: 'Jeu' },
  { key: 'fri', label: 'Vendredi', short: 'Ven' },
  { key: 'sat', label: 'Samedi',   short: 'Sam' },
  { key: 'sun', label: 'Dimanche', short: 'Dim' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Templates pré-configurés
// ─────────────────────────────────────────────────────────────────────────────

/** Semaine vide → par défaut TOUT ON (aucune coupure stockée). */
const emptyWeek = (): WeeklySchedule => ({
  mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
});

// Coupures pré-définies (les plages stockées = OFF)
const ALL_DAY_OFF: TimeSlot[] = [{ start: '00:00', end: '23:59' }];

const TEMPLATES: { id: string; icon: string; name: string; desc: string; build: () => DeviceSchedule }[] = [
  {
    id: 'office',
    icon: '🏢',
    name: 'Bureaux',
    desc: 'ON Lun–Ven · 8h00 → 18h00',
    build: () => ({
      enabled: true,
      name: 'Bureaux 8h-18h',
      updatedAt: new Date().toISOString(),
      // Coupures : nuits + weekend
      weekly: {
        mon: [{ start: '00:00', end: '08:00' }, { start: '18:00', end: '23:59' }],
        tue: [{ start: '00:00', end: '08:00' }, { start: '18:00', end: '23:59' }],
        wed: [{ start: '00:00', end: '08:00' }, { start: '18:00', end: '23:59' }],
        thu: [{ start: '00:00', end: '08:00' }, { start: '18:00', end: '23:59' }],
        fri: [{ start: '00:00', end: '08:00' }, { start: '17:00', end: '23:59' }],
        sat: [...ALL_DAY_OFF],
        sun: [...ALL_DAY_OFF],
      },
    }),
  },
  {
    id: 'factory',
    icon: '🏭',
    name: 'Usine 2x8',
    desc: 'ON Lun–Ven · 6h00 → 22h00',
    build: () => ({
      enabled: true,
      name: 'Usine 2x8',
      updatedAt: new Date().toISOString(),
      weekly: {
        mon: [{ start: '00:00', end: '06:00' }, { start: '22:00', end: '23:59' }],
        tue: [{ start: '00:00', end: '06:00' }, { start: '22:00', end: '23:59' }],
        wed: [{ start: '00:00', end: '06:00' }, { start: '22:00', end: '23:59' }],
        thu: [{ start: '00:00', end: '06:00' }, { start: '22:00', end: '23:59' }],
        fri: [{ start: '00:00', end: '06:00' }, { start: '22:00', end: '23:59' }],
        sat: [...ALL_DAY_OFF],
        sun: [...ALL_DAY_OFF],
      },
    }),
  },
  {
    id: 'shop',
    icon: '🛒',
    name: 'Commerce',
    desc: 'ON Lun–Sam · 9h00 → 20h00',
    build: () => ({
      enabled: true,
      name: 'Commerce 9h-20h',
      updatedAt: new Date().toISOString(),
      weekly: {
        mon: [{ start: '00:00', end: '09:00' }, { start: '20:00', end: '23:59' }],
        tue: [{ start: '00:00', end: '09:00' }, { start: '20:00', end: '23:59' }],
        wed: [{ start: '00:00', end: '09:00' }, { start: '20:00', end: '23:59' }],
        thu: [{ start: '00:00', end: '09:00' }, { start: '20:00', end: '23:59' }],
        fri: [{ start: '00:00', end: '09:00' }, { start: '20:00', end: '23:59' }],
        sat: [{ start: '00:00', end: '09:00' }, { start: '19:00', end: '23:59' }],
        sun: [...ALL_DAY_OFF],
      },
    }),
  },
  {
    id: 'hc',
    icon: '🌙',
    name: 'Heures creuses',
    desc: 'ON 22h → 6h (coupé en journée)',
    build: () => {
      // Coupé de 06:00 à 22:00 (ON la nuit)
      const off = [{ start: '06:00', end: '22:00' }];
      return {
        enabled: true,
        name: 'Heures creuses (ON nuit)',
        updatedAt: new Date().toISOString(),
        weekly: {
          mon: [...off], tue: [...off], wed: [...off], thu: [...off],
          fri: [...off], sat: [...off], sun: [...off],
        },
      };
    },
  },
];
void TEMPLATES;
void ALL_DAY_OFF;

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_PREFIX = 'zce.wattelem.schedule.';

function normalizeWeekly(w: Partial<WeeklySchedule> | Record<string, unknown> | undefined | null): WeeklySchedule {
  const base: WeeklySchedule = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
  if (!w || typeof w !== 'object') return base;
  for (const k of ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as DayKey[]) {
    const v = (w as Record<string, unknown>)[k];
    if (Array.isArray(v)) base[k] = v as TimeSlot[];
  }
  return base;
}

function normalizeSchedule(s: Partial<DeviceSchedule> | null | undefined): DeviceSchedule | null {
  if (!s) return null;
  return {
    enabled: typeof s.enabled === 'boolean' ? s.enabled : true,
    name: typeof s.name === 'string' && s.name.length > 0 ? s.name : translate('eq2_modal_default_name'),
    weekly: normalizeWeekly(s.weekly),
    overrides: Array.isArray(s.overrides) ? s.overrides : [],
    updatedAt: typeof s.updatedAt === 'string' ? s.updatedAt : new Date().toISOString(),
  };
}

function loadSchedule(deviceSn: string): DeviceSchedule | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + deviceSn);
    if (!raw) return null;
    return normalizeSchedule(JSON.parse(raw) as Partial<DeviceSchedule>);
  } catch {
    return null;
  }
}

export { loadSchedule };

function saveSchedule(deviceSn: string, schedule: DeviceSchedule | null) {
  try {
    if (schedule === null) localStorage.removeItem(STORAGE_PREFIX + deviceSn);
    else localStorage.setItem(STORAGE_PREFIX + deviceSn, JSON.stringify(schedule));
  } catch {
    /* ignore */
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useDeviceSchedule(deviceSn: string | null | undefined) {
  const [schedule, setSchedule] = useState<DeviceSchedule | null>(null);
  // Incrémenté par reload() : force une relecture serveur (ex. planning
  // désactivé côté serveur lors d'un passage en « compteur seul »).
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    if (!deviceSn) { setSchedule(null); return; }
    const cached = loadSchedule(deviceSn);
    setSchedule(cached);
    let cancelled = false;
    wattelEMApi.getSchedule(deviceSn)
      .then((rec) => {
        if (cancelled) return;
        if (rec) {
          const remote = normalizeSchedule({
            enabled: !!rec.enabled,
            name: rec.name,
            weekly: rec.weekly as unknown as WeeklySchedule,
            overrides: rec.overrides ?? [],
            updatedAt: rec.updatedAt,
          });
          if (remote) {
            saveSchedule(deviceSn, remote);
            setSchedule(remote);
          }
        } else if (cached) {
          wattelEMApi.saveSchedule(deviceSn, {
            enabled: cached.enabled,
            name: cached.name,
            weekly: cached.weekly as unknown as Record<string, Array<{ start: string; end: string }>>,
            overrides: cached.overrides ?? [],
          }).catch(() => undefined);
        } else {
          saveSchedule(deviceSn, null);
          setSchedule(null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [deviceSn, reloadKey]);

  const persist = useCallback((s: DeviceSchedule | null) => {
    if (!deviceSn) return;
    saveSchedule(deviceSn, s);
    setSchedule(s);
    if (s === null) {
      wattelEMApi.deleteSchedule(deviceSn).catch(() => undefined);
    } else {
      wattelEMApi.saveSchedule(deviceSn, {
        enabled: s.enabled,
        name: s.name,
        weekly: s.weekly as unknown as Record<string, Array<{ start: string; end: string }>>,
        overrides: s.overrides ?? [],
      }).catch(() => undefined);
    }
  }, [deviceSn]);

  return { schedule, setSchedule: persist, reload };
}

// ─────────────────────────────────────────────────────────────────────────────
// Logique : état attendu maintenant + prochain événement
// ─────────────────────────────────────────────────────────────────────────────

const DAY_ORDER: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function timeToMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function minToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

interface NextEvent {
  state: 0 | 1;            // état attendu maintenant
  nextState: 0 | 1;        // état après le prochain changement (= state s'il n'y en a pas)
  nextAt: Date | null;     // date du prochain changement réel ; null = aucun sous 60 jours
  minutesUntil: number;    // minutes avant ce changement (0 s'il n'y en a pas)
}

const DAY_END_MIN = 24 * 60;

/**
 * Plages ON d'une date, en minutes depuis minuit, fin exclue.
 *
 * Une fin à « 23:59 » désigne la FIN DE LA JOURNÉE (minuit) : c'est la
 * convention des plannings types et des barres de l'éditeur. Sans cette
 * normalisation, la minute 23:59 était calculée OFF et l'exécuteur coupait
 * chaque nuit, à 23h59, tous les modules ayant un planning actif.
 * Côté serveur, wattel-em.ts (onRangesForDay) applique la même règle.
 */
function onRangesInMinutes(schedule: DeviceSchedule, date: Date): Array<{ s: number; e: number }> {
  return getDaySlots(schedule, date)
    .map((slot) => {
      const s = timeToMin(slot.start);
      const e = timeToMin(slot.end);
      return { s, e: e >= DAY_END_MIN - 1 ? DAY_END_MIN : e };
    })
    .filter((r) => r.e > r.s);
}

function isOnAt(ranges: Array<{ s: number; e: number }>, minute: number): 0 | 1 {
  return ranges.some((r) => minute >= r.s && minute < r.e) ? 1 : 0;
}

export function computeNextEvent(schedule: DeviceSchedule | null, now: Date = new Date()): NextEvent | null {
  if (!schedule || !schedule.enabled) return null;

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const today = onRangesInMinutes(schedule, now);
  const state = isOnAt(today, nowMin);

  // Prochain changement RÉEL : on parcourt les bornes de plage dans l'ordre
  // chronologique et on s'arrête à la première où l'état diffère de l'état
  // actuel. Minuit (borne 0 du jour suivant) n'est donc un changement que si
  // le lendemain ne reprend pas dans le même état.
  // Fenêtre de 60 jours : couvre largement les overrides + l'hebdo.
  for (let dayOffset = 0; dayOffset < 60; dayOffset++) {
    const probe = new Date(now);
    probe.setDate(now.getDate() + dayOffset);
    const ranges = dayOffset === 0 ? today : onRangesInMinutes(schedule, probe);

    const bounds = new Set<number>([0]);
    for (const r of ranges) {
      bounds.add(r.s);
      if (r.e < DAY_END_MIN) bounds.add(r.e);
    }
    for (const bound of [...bounds].sort((x, y) => x - y)) {
      if (dayOffset === 0 && bound <= nowMin) continue;
      const after = isOnAt(ranges, bound);
      if (after !== state) {
        const at = new Date(probe);
        at.setHours(Math.floor(bound / 60), bound % 60, 0, 0);
        return {
          state,
          nextState: after,
          nextAt: at,
          minutesUntil: Math.max(0, Math.round((at.getTime() - now.getTime()) / 60000)),
        };
      }
    }
  }

  // Aucun changement dans les 60 jours (ex. planning sans aucune coupure).
  return { state, nextState: state, nextAt: null, minutesUntil: 0 };
}

// Stats hebdo (heures ON / OFF)
// Les plages stockées dans schedule.weekly[day] représentent des COUPURES (OFF).
// hoursOff = somme des coupures, hoursOn = 168 - hoursOff.
function computeWeeklyStats(schedule: DeviceSchedule | null) {
  if (!schedule) return { hoursOn: 168, hoursOff: 0 };
  let totalOff = 0;
  for (const day of DAYS) {
    for (const slot of schedule.weekly[day.key] || []) {
      const s = timeToMin(slot.start);
      const e = timeToMin(slot.end);
      if (e > s) totalOff += (e - s);
    }
  }
  const hoursOff = Math.round((totalOff / 60) * 10) / 10;
  return { hoursOn: Math.round((168 - hoursOff) * 10) / 10, hoursOff };
}

function formatDuration(minutes: number, t: TFn): string {
  if (minutes < 1) return t('eq2_dur_lt1');
  if (minutes < 60) return t('eq2_dur_min', { m: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m > 0 ? t('eq2_dur_h_min', { h, m }) : t('eq2_dur_h', { h });
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH > 0 ? t('eq2_dur_d_h', { d, h: remH }) : t('eq2_dur_d', { d });
}

// ─────────────────────────────────────────────────────────────────────────────
// ScheduleStatusCard — affichée sous Contrôle Relay
// ─────────────────────────────────────────────────────────────────────────────

export function ScheduleStatusCard({
  schedule,
  onOpen,
  onToggleEnabled,
  onDelete,
  readOnlyReason,
}: {
  schedule: DeviceSchedule | null;
  onOpen: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
  /** Renseigné = consultation seule : le planning s'affiche, aucune action n'est proposée. */
  readOnlyReason?: string | null;
}) {
  const { t, locale } = useI18n();
  const readOnly = !!readOnlyReason;
  const readOnlyNote = readOnly ? (
    <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
      <span aria-hidden>🔒</span>
      <span>{readOnlyReason}</span>
    </div>
  ) : null;
  // Re-render chaque minute pour mettre à jour le compteur
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  void tick;

  const evt = useMemo(() => computeNextEvent(schedule), [schedule, tick]);
  const stats = useMemo(() => computeWeeklyStats(schedule), [schedule]);

  if (!schedule) {
    return (
      <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq2_sched_title')}</h3>
            <p className="text-xs text-zce-gray-muted">{t('eq2_sched_none')}</p>
          </div>
        </div>
        {readOnly ? readOnlyNote : (
          <button
            onClick={onOpen}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            <span>🗓️</span>
            <span>{t('eq2_sched_create')}</span>
          </button>
        )}
      </div>
    );
  }

  const isEnabled = schedule.enabled;
  const expectedOn = evt?.state === 1;

  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq2_sched_title')}</h3>
            <p className="text-xs text-zce-gray-muted">{schedule.name}</p>
          </div>
        </div>
        {/* Toggle activé/désactivé */}
        {!readOnly && (
          <button
            onClick={onToggleEnabled}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              isEnabled ? 'bg-emerald-500' : 'bg-gray-300'
            }`}
            aria-label={isEnabled ? t('eq2_sched_disable') : t('eq2_sched_enable')}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        )}
      </div>

      {/* État planifié + prochain événement */}
      <div className={`rounded-xl p-4 mb-3 ${isEnabled ? (expectedOn ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200') : 'bg-gray-50 border border-gray-200'}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider mb-1">{t('eq2_sched_planned_state')}</p>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${isEnabled && expectedOn ? 'bg-emerald-500 animate-pulse' : isEnabled ? 'bg-rose-500' : 'bg-gray-400'}`} />
              <p className={`text-lg font-bold font-heading ${isEnabled && expectedOn ? 'text-emerald-700' : isEnabled ? 'text-rose-700' : 'text-gray-500'}`}>
                {!isEnabled ? t('eq2_sched_disabled_upper') : expectedOn ? t('eq2_state_on') : t('eq2_state_off')}
              </p>
            </div>
          </div>
          {isEnabled && evt && (
            <div className="text-right">
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider mb-1">{t('eq2_sched_next_change')}</p>
              {evt.nextAt ? (
                <>
                  <p className="text-sm font-semibold text-zce-navy">
                    {evt.nextState === 1 ? `🟢 ${t('eq2_state_on')}` : `🔴 ${t('eq2_state_off')}`} {t('eq2_sched_in')} <span className="text-zce-orange">{formatDuration(evt.minutesUntil, t)}</span>
                  </p>
                  <p className="text-xs text-zce-gray-muted mt-0.5">
                    {evt.nextAt.toLocaleString(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </>
              ) : (
                <p className="text-sm font-semibold text-zce-navy">{t('eq2_sched_none_planned')}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
          <p className="text-xs text-emerald-700 font-medium uppercase tracking-wider">{t('eq2_sched_hours_on_week')}</p>
          <p className="text-lg font-bold text-emerald-700">{stats.hoursOn} h</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">{t('eq2_sched_hours_off_week')}</p>
          <p className="text-lg font-bold text-gray-700">{stats.hoursOff} h</p>
        </div>
      </div>

      {/* Actions */}
      {readOnly ? readOnlyNote : (
        <div className="flex gap-2">
          <button
            onClick={onOpen}
            className="flex-1 py-2.5 px-4 bg-zce-navy hover:bg-zce-navy/90 text-white font-semibold rounded-lg transition-all text-sm flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('eq2_edit')}
          </button>
          <button
            onClick={onDelete}
            className="py-2.5 px-4 bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold rounded-lg transition-all text-sm border border-rose-200"
            title={t('eq2_sched_delete')}
            aria-label={t('eq2_sched_delete')}
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SchedulePlannerModal — modal d'édition
// ─────────────────────────────────────────────────────────────────────────────

export function SchedulePlannerModal({
  open,
  deviceName,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  deviceName: string;
  initial: DeviceSchedule | null;
  onClose: () => void;
  onSave: (s: DeviceSchedule) => void;
}) {
  const { t, locale } = useI18n();
  const [draft, setDraft] = useState<DeviceSchedule>(() => initial ?? {
    enabled: true,
    name: t('eq2_modal_default_name'),
    weekly: emptyWeek(),
    overrides: [],
    updatedAt: new Date().toISOString(),
  });
  const [selectedWeekIdx, setSelectedWeekIdx] = useState<number>(() => {
    // 0=lundi de cette semaine ... 6=dimanche
    const today = new Date();
    return (today.getDay() + 6) % 7;
  });
  const [activeTab, setActiveTab] = useState<'weekly' | 'calendar'>('weekly');
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);

  // Reset quand on ouvre
  useEffect(() => {
    if (open) {
      setDraft(initial ?? {
        enabled: true,
        name: t('eq2_modal_default_name'),
        weekly: emptyWeek(),
        overrides: [],
        updatedAt: new Date().toISOString(),
      });
      const today = new Date();
      setSelectedWeekIdx((today.getDay() + 6) % 7);
      setActiveTab('weekly');
      const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
      setCalendarMonth(d);
      setSelectedCalendarDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  // ── Dates de la semaine en cours (lun → dim) ──
  const currentWeekDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dow = (today.getDay() + 6) % 7; // 0=lun
    const monday = new Date(today);
    monday.setDate(today.getDate() - dow);
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      out.push(d);
    }
    return out;
  }, [open]); // recalculé à chaque ouverture

  // ── Helpers Overrides datés (édits = toujours overrides) ──
  const upsertOverride = (date: Date, ov: Omit<DateOverride, 'date'>) => {
    const key = dateKey(date);
    setDraft(d => {
      const list = (d.overrides || []).filter(o => o.date !== key);
      return { ...d, overrides: [...list, { ...ov, date: key }] };
    });
  };
  const removeOverride = (date: Date) => {
    const key = dateKey(date);
    setDraft(d => ({ ...d, overrides: (d.overrides || []).filter(o => o.date !== key) }));
  };
  const findOverride = (date: Date): DateOverride | undefined => {
    const key = dateKey(date);
    return (draft.overrides || []).find(o => o.date === key);
  };

  /** Lit les coupures (OFF) effectives d'une date : override custom → slots ; all-off → 24h ; all-on/none → [] */
  const getDateOffSlots = (date: Date): TimeSlot[] => {
    const ov = findOverride(date);
    if (!ov) return [];
    if (ov.mode === 'all-off') return [{ start: '00:00', end: '23:59' }];
    if (ov.mode === 'all-on') return [];
    return ov.slots || [];
  };

  /** Écrit les coupures sur une date. Slots vide ⇒ supprime l'override (revient à ON 24h). */
  const setDateOffSlots = (date: Date, slots: TimeSlot[]) => {
    if (slots.length === 0) removeOverride(date);
    else upsertOverride(date, { mode: 'custom', slots });
  };

  // ── Actions sur la semaine en cours ──
  const addSlotToDate = (date: Date) => {
    const current = getDateOffSlots(date);
    const last = current[current.length - 1];
    const newSlot: TimeSlot = last
      ? { start: last.end, end: minToTime(Math.min(timeToMin(last.end) + 60, 23 * 60 + 59)) }
      : { start: '22:00', end: '23:59' };
    setDateOffSlots(date, [...current, newSlot]);
  };
  const removeSlotFromDate = (date: Date, idx: number) => {
    setDateOffSlots(date, getDateOffSlots(date).filter((_, i) => i !== idx));
  };
  const updateSlotOnDate = (date: Date, idx: number, patch: Partial<TimeSlot>) => {
    setDateOffSlots(date, getDateOffSlots(date).map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  /** Copie les coupures du Lundi de cette semaine vers Mar-Ven de cette semaine. */
  const copyMondayToWeekdays = () => {
    const monday = currentWeekDates[0];
    const slots = getDateOffSlots(monday);
    setDraft(d => {
      const targetKeys = [1, 2, 3, 4].map(i => dateKey(currentWeekDates[i]));
      const filtered = (d.overrides || []).filter(o => !targetKeys.includes(o.date));
      const additions: DateOverride[] = [];
      if (slots.length > 0) {
        for (const k of targetKeys) additions.push({ date: k, mode: 'custom', slots: slots.map(s => ({ ...s })) });
      }
      return { ...d, overrides: [...filtered, ...additions] };
    });
  };

  /** Supprime tous les overrides de la semaine en cours (revient à ON 24h). */
  const clearCurrentWeek = () => {
    setDraft(d => {
      const weekKeys = currentWeekDates.map(dt => dateKey(dt));
      return { ...d, overrides: (d.overrides || []).filter(o => !weekKeys.includes(o.date)) };
    });
  };

  const handleSave = () => {
    onSave({ ...draft, updatedAt: new Date().toISOString() });
    onClose();
  };

  // Timeline 24h pour la date sélectionnée (semaine en cours)
  const selectedDate = currentWeekDates[selectedWeekIdx] ?? currentWeekDates[0];
  const timelineSegments = useMemo(() => {
    const stored = getDateOffSlots(selectedDate);
    if (stored.length === 0) {
      return [{ start: 0, end: 24 * 60, on: true }];
    }
    const offSorted = stored
      .map(s => ({ s: timeToMin(s.start), e: timeToMin(s.end) }))
      .filter(x => x.e > x.s)
      .sort((a, b) => a.s - b.s);
    const segs: { start: number; end: number; on: boolean }[] = [];
    let cursor = 0;
    for (const off of offSorted) {
      if (off.s > cursor) segs.push({ start: cursor, end: off.s, on: true });
      segs.push({ start: Math.max(off.s, cursor), end: off.e, on: false });
      cursor = Math.max(cursor, off.e);
    }
    if (cursor < 24 * 60) segs.push({ start: cursor, end: 24 * 60, on: true });
    return segs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, selectedDate]);

  // Stats : on totalise les ON/OFF de la semaine en cours
  const stats = useMemo(() => {
    let totalOff = 0;
    for (const dt of currentWeekDates) {
      for (const slot of getDateOffSlots(dt)) {
        const s = timeToMin(slot.start);
        const e = timeToMin(slot.end);
        if (e > s) totalOff += (e - s);
      }
    }
    const hoursOff = Math.round((totalOff / 60) * 10) / 10;
    return { hoursOn: Math.round((168 - hoursOff) * 10) / 10, hoursOff };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, currentWeekDates]);

  // ── Mini-calendrier 7 jours = la semaine en cours ──
  const now = new Date();
  const weekDays = useMemo(() => {
    const out: { date: Date; isToday: boolean; segments: { startMin: number; endMin: number; on: boolean }[]; hoursOn: number }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = currentWeekDates[i];
      const stored = getDateOffSlots(d);
      const offSorted = stored
        .map(s => ({ s: timeToMin(s.start), e: timeToMin(s.end) }))
        .filter(x => x.e > x.s)
        .sort((a, b) => a.s - b.s);
      const segments: { startMin: number; endMin: number; on: boolean }[] = [];
      let cursor = 0;
      let offMin = 0;
      for (const off of offSorted) {
        if (off.s > cursor) segments.push({ startMin: cursor, endMin: off.s, on: true });
        segments.push({ startMin: Math.max(off.s, cursor), endMin: off.e, on: false });
        offMin += off.e - Math.max(off.s, cursor);
        cursor = Math.max(cursor, off.e);
      }
      if (cursor < 24 * 60) segments.push({ startMin: cursor, endMin: 24 * 60, on: true });
      const onMin = 24 * 60 - offMin;
      out.push({ date: d, isToday: sameDay(d, now), segments, hoursOn: Math.round((onMin / 60) * 10) / 10 });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, currentWeekDates]);

  // État planifié à l'instant T pour aujourd'hui
  const todayEvent = useMemo(() => computeNextEvent(draft, now), [draft, now]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-zce-navy to-blue-800 text-white px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center text-2xl">
              🗓️
            </div>
            <div>
              <h2 className="text-xl font-bold font-heading">{t('eq2_sched_title')}</h2>
              <p className="text-xs text-white/80">{t('eq2_modal_device')} <span className="font-semibold">{deviceName}</span></p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            aria-label={t('eq2_close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Nom + activation */}
          <div className="flex items-center gap-3 mb-5">
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
              placeholder={t('eq2_modal_name_ph')}
              className="flex-1 px-4 py-2.5 border-2 border-gray-200 focus:border-blue-500 rounded-lg outline-none font-semibold text-zce-navy"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm font-semibold text-zce-gray-muted">{t('eq2_modal_active')}</span>
              <button
                type="button"
                onClick={() => setDraft(d => ({ ...d, enabled: !d.enabled }))}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${draft.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${draft.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
          </div>

          {/* ─── Tabs ─── */}
          <div className="flex gap-1 mb-5 border-b-2 border-gray-200">
            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-5 py-3 font-semibold text-sm transition-all border-b-2 -mb-0.5 ${
                activeTab === 'weekly'
                  ? 'border-blue-500 text-blue-700'
                  : 'border-transparent text-zce-gray-muted hover:text-zce-navy'
              }`}
            >
              {t('eq2_modal_tab_week')}
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-5 py-3 font-semibold text-sm transition-all border-b-2 -mb-0.5 flex items-center gap-2 ${
                activeTab === 'calendar'
                  ? 'border-blue-500 text-blue-700'
                  : 'border-transparent text-zce-gray-muted hover:text-zce-navy'
              }`}
            >
              {t('eq2_modal_tab_calendar')}
              {(draft.overrides?.length ?? 0) > 0 && (
                <span className="bg-orange-100 text-orange-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {draft.overrides!.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'weekly' && (<>
          {/* ─── Aujourd'hui + Calendrier 7 jours ─── */}
          <div className="mb-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">{t('eq2_modal_today_upper')}</p>
                <p className="text-base font-bold text-zce-navy">
                  {now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}
                  <span className="text-zce-orange">{now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
                </p>
              </div>
              {todayEvent && draft.enabled && (
                <div className="text-right">
                  <p className="text-xs font-semibold uppercase tracking-wider text-zce-gray-muted mb-0.5">{t('eq2_modal_planned_now')}</p>
                  <div className="flex items-center justify-end gap-2">
                    <span className={`w-3 h-3 rounded-full ${todayEvent.state === 1 ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                    <span className={`text-base font-bold ${todayEvent.state === 1 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {todayEvent.state === 1 ? t('eq2_state_on') : t('eq2_state_off')}
                    </span>
                    {todayEvent.nextAt && (
                      <span className="text-xs text-zce-gray-muted">
                        {t('eq2_modal_next_at', {
                          state: todayEvent.nextState === 1 ? t('eq2_state_on') : t('eq2_state_off'),
                          time: todayEvent.nextAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
                        })}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Calendrier 7 prochains jours */}
            <div className="grid grid-cols-7 gap-1.5">
              {weekDays.map((day, i) => {
                const isSelected = selectedWeekIdx === i;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedWeekIdx(i)}
                    className={`p-2 rounded-lg border-2 transition-all text-left ${
                      day.isToday
                        ? 'border-blue-500 bg-white shadow-md ring-2 ring-blue-200'
                        : isSelected
                          ? 'border-blue-400 bg-white'
                          : 'border-gray-200 bg-white/70 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] uppercase font-bold ${day.isToday ? 'text-blue-700' : 'text-zce-gray-muted'}`}>
                        {day.date.toLocaleDateString(locale, { weekday: 'short' })}
                      </span>
                      {day.isToday && (
                        <span className="text-[8px] font-bold bg-blue-500 text-white px-1 py-0.5 rounded">{t('eq2_modal_today_badge')}</span>
                      )}
                    </div>
                    <div className={`text-lg font-bold ${day.isToday ? 'text-blue-700' : 'text-zce-navy'}`}>
                      {day.date.getDate()}
                    </div>
                    {/* Mini-barre 24h */}
                    <div className="h-1.5 rounded-full overflow-hidden bg-gray-200 flex mt-1">
                      {day.segments.map((seg, j) => (
                        <div
                          key={j}
                          style={{ width: `${((seg.endMin - seg.startMin) / (24 * 60)) * 100}%` }}
                          className={seg.on ? 'bg-emerald-500' : 'bg-gray-300'}
                        />
                      ))}
                    </div>
                    <div className="text-[10px] text-zce-gray-muted mt-1 text-center font-medium">
                      {t('eq3_day_hours_on', { n: day.hoursOn })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions rapides */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <button
              onClick={copyMondayToWeekdays}
              className="px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg border border-blue-200"
              title={t('eq2_modal_copy_mon_title')}
            >
              {t('eq2_modal_copy_mon')}
            </button>
            <button
              onClick={() => {
                if (window.confirm(t('eq2_modal_reset_confirm'))) {
                  clearCurrentWeek();
                }
              }}
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg border border-emerald-200"
              title={t('eq2_modal_reset_title')}
            >
              {t('eq2_modal_reset')}
            </button>
            <div className="ml-auto flex items-center gap-2 text-xs text-zce-gray-muted">
              <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded font-semibold">{t('eq2_modal_h_on_week', { n: stats.hoursOn })}</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-semibold">{t('eq2_modal_h_off_week', { n: stats.hoursOff })}</span>
            </div>
          </div>

          {/* Grille jours de la semaine en cours */}
          <div className="space-y-2 mb-6">
            {currentWeekDates.map((date, i) => {
              const slots = getDateOffSlots(date);
              const isSelected = selectedWeekIdx === i;
              const isToday = sameDay(date, now);
              const dayLabel = date.toLocaleDateString(locale, { weekday: 'long' });
              const dateLabel = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
              return (
                <div
                  key={i}
                  className={`border-2 rounded-xl p-3 transition-all ${isSelected ? 'border-blue-500 bg-blue-50/50' : isToday ? 'border-blue-300 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => setSelectedWeekIdx(i)}
                      className={`min-w-[140px] text-left px-3 py-2 rounded-lg transition ${isSelected ? 'bg-blue-500 text-white' : 'text-zce-navy hover:bg-gray-100'}`}
                    >
                      <div className="font-bold capitalize text-sm">{dayLabel}</div>
                      <div className={`text-[11px] ${isSelected ? 'text-white/80' : 'text-zce-gray-muted'}`}>
                        {dateLabel}{isToday && t('eq2_modal_today_suffix')}
                      </div>
                    </button>

                    <div className="flex-1 flex flex-wrap gap-2">
                      {slots.length === 0 && (
                        <span className="text-sm text-emerald-600 italic px-2 py-2 flex items-center gap-1">
                          {t('eq2_modal_always_on')} <span className="text-xs text-zce-gray-muted">{t('eq2_modal_always_on_hint')}</span>
                        </span>
                      )}
                      {slots.map((slot, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1.5"
                          title={t('eq2_modal_cutoff_title')}
                        >
                          <span className="text-[10px] font-bold text-rose-600 uppercase mr-1">{t('eq2_state_off')}</span>
                          <input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateSlotOnDate(date, idx, { start: e.target.value })}
                            className="bg-transparent text-sm font-semibold text-rose-700 outline-none w-[75px]"
                          />
                          <span className="text-rose-600 font-bold">→</span>
                          <input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateSlotOnDate(date, idx, { end: e.target.value })}
                            className="bg-transparent text-sm font-semibold text-rose-700 outline-none w-[75px]"
                          />
                          <button
                            onClick={() => removeSlotFromDate(date, idx)}
                            className="ml-1 w-6 h-6 rounded-full bg-rose-200 hover:bg-rose-300 text-rose-800 text-xs font-bold flex items-center justify-center transition"
                            aria-label={t('eq2_modal_cutoff_delete')}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addSlotToDate(date)}
                        className="px-3 py-1.5 text-sm font-semibold bg-white border-2 border-dashed border-rose-300 hover:border-rose-500 hover:text-rose-600 text-zce-gray-muted rounded-lg transition"
                        title={t('eq2_modal_cutoff_add_title')}
                      >
                        {t('eq2_modal_cutoff_add')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Timeline 24h du jour sélectionné */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-zce-gray-muted mb-2">
              {t('eq2_modal_preview')} <span className="capitalize">{selectedDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'short' })}</span>
            </p>
            <div className="relative h-10 rounded-lg overflow-hidden bg-gray-200 flex">
              {timelineSegments.map((seg, i) => {
                const widthPct = ((seg.end - seg.start) / (24 * 60)) * 100;
                return (
                  <div
                    key={i}
                    style={{ width: `${widthPct}%` }}
                    className={`h-full ${seg.on ? 'bg-gradient-to-b from-emerald-400 to-emerald-500' : 'bg-gray-300'} flex items-center justify-center`}
                    title={`${minToTime(seg.start)} → ${minToTime(seg.end)} : ${seg.on ? t('eq2_state_on') : t('eq2_state_off')}`}
                  >
                    {widthPct > 8 && (
                      <span className={`text-[10px] font-bold ${seg.on ? 'text-white' : 'text-gray-500'}`}>
                        {seg.on ? t('eq2_state_on') : t('eq2_state_off')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-zce-gray-muted font-medium">
              {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => (
                <span key={h}>{t('eq2_hour_tick', { h: String(h).padStart(2, '0') })}</span>
              ))}
            </div>
          </div>
          </>)}

          {activeTab === 'calendar' && (
            <CalendarMonthView
              draft={draft}
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              selectedDate={selectedCalendarDate}
              setSelectedDate={setSelectedCalendarDate}
              findOverride={findOverride}
              upsertOverride={upsertOverride}
              removeOverride={removeOverride}
              onApplyForDuration={(srcDate, days) => {
                // Copie l'état (override ou défaut) de srcDate sur srcDate+1 → srcDate+days
                setDraft(d => {
                  const ov = (d.overrides || []).find(o => o.date === dateKey(srcDate));
                  const targetKeys: string[] = [];
                  for (let i = 1; i <= days; i++) targetKeys.push(dateKey(addDays(srcDate, i)));
                  const filtered = (d.overrides || []).filter(o => !targetKeys.includes(o.date));
                  if (!ov) {
                    // src = défaut (ON 24h) ⇒ on supprime juste les overrides sur la plage
                    return { ...d, overrides: filtered };
                  }
                  const additions: DateOverride[] = targetKeys.map(k => ({
                    date: k,
                    mode: ov.mode,
                    slots: ov.mode === 'custom' && ov.slots ? ov.slots.map(s => ({ ...s })) : undefined,
                    note: ov.note,
                  }));
                  return { ...d, overrides: [...filtered, ...additions] };
                });
              }}
            />
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-xs text-zce-gray-muted">
            {t('eq2_modal_footer_note')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-zce-gray-muted hover:text-zce-navy rounded-lg hover:bg-gray-200 transition"
            >
              {t('eq2_cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg shadow hover:shadow-md transition flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('eq2_modal_save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// placeholder appended

// ─────────────────────────────────────────────────────────────────────────────
// CalendarMonthView — vue mensuelle complète avec navigation + override editor
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_KEYS = [
  'eq2_month_0', 'eq2_month_1', 'eq2_month_2', 'eq2_month_3', 'eq2_month_4', 'eq2_month_5',
  'eq2_month_6', 'eq2_month_7', 'eq2_month_8', 'eq2_month_9', 'eq2_month_10', 'eq2_month_11',
] as const;
const DOW_SHORT_KEYS = ['eq2_dow_mon', 'eq2_dow_tue', 'eq2_dow_wed', 'eq2_dow_thu', 'eq2_dow_fri', 'eq2_dow_sat', 'eq2_dow_sun'] as const;

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function CalendarMonthView({
  draft,
  calendarMonth,
  setCalendarMonth,
  selectedDate,
  setSelectedDate,
  findOverride,
  upsertOverride,
  removeOverride,
  onApplyForDuration,
}: {
  draft: DeviceSchedule;
  calendarMonth: Date;
  setCalendarMonth: (d: Date) => void;
  selectedDate: Date | null;
  setSelectedDate: (d: Date | null) => void;
  findOverride: (date: Date) => DateOverride | undefined;
  upsertOverride: (date: Date, ov: Omit<DateOverride, 'date'>) => void;
  removeOverride: (date: Date) => void;
  onApplyForDuration: (srcDate: Date, days: number) => void;
}) {
  const { t } = useI18n();
  const today = new Date();

  // Construire la grille du mois (semaines commencent le lundi)
  const monthCells = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // getDay : 0=dim, 1=lun, ..., 6=sam → on veut lundi=0
    const firstDow = (firstOfMonth.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - firstDow);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [calendarMonth]);

  const goPrev = () => {
    const d = new Date(calendarMonth);
    d.setMonth(d.getMonth() - 1);
    setCalendarMonth(d);
  };
  const goNext = () => {
    const d = new Date(calendarMonth);
    d.setMonth(d.getMonth() + 1);
    setCalendarMonth(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setDate(1); d.setHours(0, 0, 0, 0);
    setCalendarMonth(d);
    setSelectedDate(new Date());
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* ── Mini-calendrier (2/3) ── */}
      <div className="lg:col-span-2 bg-white border-2 border-gray-200 rounded-xl p-4">
        {/* Nav mois */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={goPrev}
            className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center text-zce-navy font-bold transition"
            aria-label={t('eq2_cal_prev_month')}
          >‹</button>
          <div className="flex items-center gap-2">
            <select
              value={calendarMonth.getMonth()}
              onChange={(e) => {
                const d = new Date(calendarMonth);
                d.setMonth(Number(e.target.value));
                setCalendarMonth(d);
              }}
              className="px-3 py-2 text-base font-bold font-heading text-zce-navy bg-white border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 rounded-lg outline-none cursor-pointer transition"
            >
              {MONTH_KEYS.map((m, i) => (
                <option key={i} value={i}>{t(m)}</option>
              ))}
            </select>
            <select
              value={calendarMonth.getFullYear()}
              onChange={(e) => {
                const d = new Date(calendarMonth);
                d.setFullYear(Number(e.target.value));
                setCalendarMonth(d);
              }}
              className="px-3 py-2 text-base font-bold font-heading text-zce-navy bg-white border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 rounded-lg outline-none cursor-pointer transition"
            >
              {(() => {
                const currentYear = new Date().getFullYear();
                const years: number[] = [];
                for (let y = currentYear - 2; y <= currentYear + 10; y++) years.push(y);
                return years.map(y => <option key={y} value={y}>{y}</option>);
              })()}
            </select>
            <button
              onClick={goToday}
              className="text-xs font-semibold px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg border border-blue-200"
            >
              {t('eq2_today')}
            </button>
          </div>
          <button
            onClick={goNext}
            className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center text-zce-navy font-bold transition"
            aria-label={t('eq2_cal_next_month')}
          >›</button>
        </div>

        {/* En-têtes jours */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW_SHORT_KEYS.map(d => (
            <div key={d} className="text-center text-[11px] font-bold uppercase text-zce-gray-muted py-1">{t(d)}</div>
          ))}
        </div>

        {/* Grille jours */}
        <div className="grid grid-cols-7 gap-1">
          {monthCells.map((d, i) => {
            const isCurrentMonth = d.getMonth() === calendarMonth.getMonth();
            const isToday = sameDay(d, today);
            const isSelected = selectedDate && sameDay(d, selectedDate);
            const ov = findOverride(d);
            const slots = getDaySlots(draft, d);
            const onMin = slots.reduce((acc, s) => {
              const a = timeToMin(s.start), b = timeToMin(s.end);
              return acc + Math.max(0, b - a);
            }, 0);
            const hoursOn = Math.round((onMin / 60) * 10) / 10;
            const isAllOff = slots.length === 0;
            const isAllOn = slots.length === 1 && slots[0].start === '00:00' && (slots[0].end === '23:59' || slots[0].end === '24:00');

            return (
              <button
                key={i}
                onClick={() => setSelectedDate(d)}
                className={`relative aspect-square rounded-lg p-1.5 text-left transition-all border-2 ${
                  !isCurrentMonth
                    ? 'bg-gray-50 border-gray-100 text-gray-300'
                    : isSelected
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300'
                      : isToday
                        ? 'border-blue-400 bg-white shadow'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${
                    !isCurrentMonth ? 'text-gray-300' : isToday ? 'text-blue-700' : 'text-zce-navy'
                  }`}>{d.getDate()}</span>
                  {ov && (
                    <span
                      className={`w-2 h-2 rounded-full ${
                        ov.mode === 'all-off' ? 'bg-rose-500' : ov.mode === 'all-on' ? 'bg-emerald-500' : 'bg-orange-500'
                      }`}
                      title={`${t('eq2_cal_override_title', { mode: ov.mode === 'all-off' ? t('eq2_ov_label_all_off') : ov.mode === 'all-on' ? t('eq2_ov_label_all_on') : t('eq2_ov_opt_custom') })}${ov.note ? ' — ' + ov.note : ''}`}
                    />
                  )}
                </div>
                {isCurrentMonth && (
                  <>
                    {/* Mini-barre 24h */}
                    <div className="absolute bottom-1.5 left-1.5 right-1.5">
                      {isAllOff ? (
                        ov?.mode === 'all-off' ? (
                          <div className="h-1 rounded-full bg-rose-400" title={t('eq2_cal_override_all_off')} />
                        ) : (
                          <div className="h-1 rounded-full bg-gray-200" title={t('eq2_cal_follows_weekly')} />
                        )
                      ) : isAllOn ? (
                        <div className="h-1 rounded-full bg-emerald-500" title={t('eq2_cal_on_all_day')} />
                      ) : (
                        <DayMiniBar slots={slots} />
                      )}
                      <div className={`text-[9px] text-center mt-0.5 font-medium ${
                        isAllOff && !ov ? 'text-gray-400 italic' : 'text-zce-gray-muted'
                      }`}>
                        {isAllOff && !ov ? t('eq2_cal_weekly_short') : `${hoursOn} h`}
                      </div>
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Légende */}
        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100 text-[11px] text-zce-gray-muted">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {t('eq2_cal_legend_all_on')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> {t('eq2_cal_legend_all_off')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> {t('eq2_cal_legend_custom')}</span>
        </div>
      </div>

      {/* ── Panneau d'édition du jour sélectionné (1/3) ── */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
        {!selectedDate ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">📅</div>
            <p className="text-sm font-semibold text-zce-navy">{t('eq2_cal_select_date')}</p>
            <p className="text-xs text-zce-gray-muted mt-1">{t('eq2_cal_select_date_hint')}</p>
          </div>
        ) : (
          <DateOverrideEditor
            date={selectedDate}
            override={findOverride(selectedDate)}
            onSet={(ov) => upsertOverride(selectedDate, ov)}
            onReset={() => removeOverride(selectedDate)}
            onApplyDuration={(days) => onApplyForDuration(selectedDate, days)}
          />
        )}
      </div>
    </div>
  );
}

function DayMiniBar({ slots }: { slots: TimeSlot[] }) {
  const sorted = [...slots].sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
  const segs: { w: number; on: boolean }[] = [];
  let cursor = 0;
  for (const s of sorted) {
    const a = timeToMin(s.start), b = timeToMin(s.end);
    if (b <= a) continue;
    if (a > cursor) segs.push({ w: a - cursor, on: false });
    segs.push({ w: b - Math.max(a, cursor), on: true });
    cursor = Math.max(cursor, b);
  }
  if (cursor < 24 * 60) segs.push({ w: 24 * 60 - cursor, on: false });
  return (
    <div className="h-1 rounded-full overflow-hidden bg-gray-200 flex">
      {segs.map((s, i) => (
        <div key={i} style={{ width: `${(s.w / (24 * 60)) * 100}%` }} className={s.on ? 'bg-emerald-500' : 'bg-gray-300'} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DateOverrideEditor — édition de l'override d'un jour précis
// ─────────────────────────────────────────────────────────────────────────────

function DateOverrideEditor({
  date,
  override,
  onSet,
  onReset,
  onApplyDuration,
}: {
  date: Date;
  override: DateOverride | undefined;
  onSet: (ov: Omit<DateOverride, 'date'>) => void;
  onReset: () => void;
  onApplyDuration: (days: number) => void;
}) {
  const { t, tp, locale } = useI18n();
  // mode 'default' = aucun override = ON 24h
  const mode: 'default' | DateOverrideMode = override ? override.mode : 'default';
  const customSlots = override?.mode === 'custom' ? override.slots || [] : [];
  const note = override?.note || '';

  const setMode = (m: 'default' | DateOverrideMode) => {
    if (m === 'default') { onReset(); return; }
    if (m === 'all-off') onSet({ mode: 'all-off', note });
    else if (m === 'all-on') onSet({ mode: 'all-on', note });
    else onSet({ mode: 'custom', slots: customSlots.length ? customSlots : [{ start: '22:00', end: '23:59' }], note });
  };

  const updateSlots = (slots: TimeSlot[]) => {
    onSet({ mode: 'custom', slots, note });
  };

  const setNote = (n: string) => {
    if (!override) onSet({ mode: 'custom', slots: customSlots, note: n });
    else onSet({ mode: override.mode, slots: override.slots, note: n });
  };

  // Description courte de la config courante (pour le bouton "Appliquer")
  const configLabel =
    mode === 'default' ? t('eq2_ov_label_default') :
    mode === 'all-on'  ? t('eq2_ov_label_all_on') :
    mode === 'all-off' ? t('eq2_ov_label_all_off') :
    tp('eq2_ov_cutoffs', customSlots.length);

  return (
    <div>
      <div className="mb-3 pb-3 border-b border-gray-100">
        <p className="text-xs uppercase font-bold text-zce-gray-muted tracking-wider">{t('eq2_ov_date')}</p>
        <p className="text-base font-bold text-zce-navy capitalize">
          {date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Mode selector */}
      <p className="text-xs uppercase font-bold text-zce-gray-muted tracking-wider mb-2">{t('eq2_ov_day_state')}</p>
      <div className="space-y-1.5 mb-4">
        {([
          { key: 'default', icon: '�', label: t('eq2_ov_opt_default'), desc: t('eq2_ov_opt_default_desc') },
          { key: 'all-off', icon: '�', label: t('eq2_ov_label_all_off'), desc: t('eq2_ov_opt_all_off_desc') },
          { key: 'custom',  icon: '⚙️', label: t('eq2_ov_opt_custom'), desc: t('eq2_ov_opt_custom_desc') },
        ] as const).map(opt => (
          <button
            key={opt.key}
            onClick={() => setMode(opt.key)}
            className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left transition-all border-2 ${
              mode === opt.key
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-blue-300'
            }`}
          >
            <span className="text-lg">{opt.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-zce-navy">{opt.label}</div>
              <div className="text-[11px] text-zce-gray-muted truncate">{opt.desc}</div>
            </div>
            {mode === opt.key && <span className="text-blue-500">✓</span>}
          </button>
        ))}
      </div>

      {/* Slots éditables si mode = custom */}
      {mode === 'custom' && (
        <div className="mb-4">
          <p className="text-xs uppercase font-bold text-zce-gray-muted tracking-wider mb-2">{t('eq2_ov_off_slots')}</p>
          <p className="text-[11px] text-zce-gray-muted mb-2 italic">{t('eq2_ov_rest_on')}</p>
          <div className="space-y-2">
            {customSlots.map((slot, idx) => (
              <div key={idx} className="flex items-center gap-1 bg-rose-50 border border-rose-200 rounded-lg p-1.5">
                <span className="text-[10px] font-bold text-rose-600 uppercase mx-1">{t('eq2_state_off')}</span>
                <input
                  type="time"
                  value={slot.start}
                  onChange={(e) => updateSlots(customSlots.map((s, i) => i === idx ? { ...s, start: e.target.value } : s))}
                  className="bg-transparent text-sm font-semibold text-rose-700 outline-none w-[80px]"
                />
                <span className="text-rose-600 font-bold">→</span>
                <input
                  type="time"
                  value={slot.end}
                  onChange={(e) => updateSlots(customSlots.map((s, i) => i === idx ? { ...s, end: e.target.value } : s))}
                  className="bg-transparent text-sm font-semibold text-rose-700 outline-none w-[80px]"
                />
                <button
                  onClick={() => updateSlots(customSlots.filter((_, i) => i !== idx))}
                  aria-label={t('eq2_modal_cutoff_delete')}
                  className="ml-auto w-6 h-6 rounded-full bg-rose-200 hover:bg-rose-300 text-rose-800 text-xs font-bold flex items-center justify-center transition"
                >✕</button>
              </div>
            ))}
            <button
              onClick={() => {
                const last = customSlots[customSlots.length - 1];
                const next: TimeSlot = last
                  ? { start: last.end, end: minToTime(Math.min(timeToMin(last.end) + 60, 23 * 60 + 59)) }
                  : { start: '22:00', end: '23:59' };
                updateSlots([...customSlots, next]);
              }}
              className="w-full px-3 py-2 text-sm font-semibold bg-white border-2 border-dashed border-rose-300 hover:border-rose-500 hover:text-rose-600 text-zce-gray-muted rounded-lg transition"
            >
              {t('eq2_ov_add_cutoff')}
            </button>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="mb-4">
        <p className="text-xs uppercase font-bold text-zce-gray-muted tracking-wider mb-2">{t('eq2_ov_note')}</p>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('eq2_ov_note_ph')}
          className="w-full px-3 py-2 border-2 border-gray-200 focus:border-blue-500 rounded-lg outline-none text-sm"
          disabled={!override}
        />
      </div>

      {/* ─── Appliquer pour une durée ─── */}
      <div className="mb-4 border-t border-gray-100 pt-4">
        <p className="text-xs uppercase font-bold text-zce-gray-muted tracking-wider mb-1">{t('eq2_ov_apply_for')}</p>
        <p className="text-[11px] text-zce-gray-muted mb-2 italic">
          {t('eq2_ov_apply_copy_before')}<span className="font-semibold text-zce-navy">{configLabel}</span>{t('eq2_ov_apply_copy_after')}
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { days: 7,   label: t('eq2_ov_1_week'),   icon: '📅' },
            { days: 14,  label: t('eq2_ov_2_weeks'),  icon: '🗓️' },
            { days: 30,  label: t('eq2_ov_1_month'),  icon: '📆' },
            { days: 365, label: t('eq2_ov_1_year'),   icon: '♾️' },
          ] as const).map(opt => (
            <button
              key={opt.days}
              onClick={() => {
                if (window.confirm(t('eq2_ov_apply_confirm', { label: configLabel, n: opt.days, period: opt.label }))) {
                  onApplyDuration(opt.days);
                }
              }}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 text-blue-700 font-semibold text-sm transition"
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Reset */}
      {override && (
        <button
          onClick={onReset}
          className="w-full py-2 text-sm font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg border border-rose-200 transition"
        >
          {t('eq2_ov_reset')}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useScheduleExecutor — exécute réellement les plannings (envoi commandes relai)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pour chaque device passé en paramètre, lit son planning (serveur, cache local
 * en secours), calcule l'état attendu maintenant, et si différent de l'état
 * actuel du relai, dispatche la commande via `sendRelay` avec source='schedule'.
 *
 * Tourne en boucle toutes les `intervalMs` (par défaut 30 s).
 * Throttling : ne renvoie pas la même commande pour un device avant `cooldownMs` (90 s).
 */
export function useScheduleExecutor(
  devices: Array<{ device_sn: string; relay_state: number | null }>,
  sendRelay: (sn: string, value: 0 | 1, opts?: { source?: 'schedule' }) => Promise<void> | void,
  options: { intervalMs?: number; cooldownMs?: number; enabled?: boolean } = {},
) {
  const { intervalMs = 30_000, cooldownMs = 90_000, enabled = true } = options;
  // Map deviceSn -> { lastValue, lastSentAt }
  const lastSentRef = useState(() => new Map<string, { value: 0 | 1; at: number }>())[0];

  // ── Plannings lus sur le serveur (source de vérité) ───────────────────────
  // Le cache localStorage n'est rafraîchi que pour le device affiché : un
  // planning modifié ou désactivé ailleurs (autre utilisateur, passage en
  // « compteur seul ») resterait actif ici et couperait le module à tort.
  // null = pas encore chargé → on retombe sur le cache local.
  const serverSchedulesRef = useRef<Map<string, DeviceSchedule> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const refresh = () => {
      wattelEMApi.listSchedules()
        .then((list) => {
          if (cancelled) return;
          const map = new Map<string, DeviceSchedule>();
          for (const rec of list) {
            const s = normalizeSchedule({
              enabled: !!rec.enabled,
              name: rec.name,
              weekly: rec.weekly as unknown as WeeklySchedule,
              overrides: rec.overrides ?? [],
              updatedAt: rec.updatedAt,
            });
            if (s) {
              map.set(rec.device_sn, s);
              saveSchedule(rec.device_sn, s);
            }
          }
          serverSchedulesRef.current = map;
        })
        .catch(() => { /* garde la dernière version connue */ });
    };
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, intervalMs]);

  const scheduleFor = useCallback((sn: string): DeviceSchedule | null => {
    const server = serverSchedulesRef.current;
    return server ? (server.get(sn) ?? null) : loadSchedule(sn);
  }, []);

  // ── Override manuel prioritaire (ARRÊT ou DÉMARRAGE forcé) ────────────────
  // ⚠ useRef (pas useState) → écriture SYNCHRONE, le tick suivant voit
  //   immédiatement la valeur (pas de race condition avec le setInterval).
  // Sémantique :
  //   • Clic ON  → hold = 1, snapshot l'état planifié actuel
  //   • Clic OFF → hold = 0, snapshot l'état planifié actuel
  //   • Auto-release : dès que l'état planifié CHANGE par rapport au snapshot
  //     (= prochain événement de planning passé), on libère le hold et la
  //     planification reprend automatiquement.
  const manualHoldRef = useRef(new Map<string, 0 | 1>());
  const holdSnapshotRef = useRef(new Map<string, 0 | 1>());

  const forceManual = useCallback((sn: string, value: 0 | 1) => {
    if (!sn) return;
    manualHoldRef.current.set(sn, value);
    // Snapshot l'état planifié actuel pour détecter le prochain changement
    const sch = scheduleFor(sn);
    if (sch) {
      const evt = computeNextEvent(sch, new Date());
      if (evt) holdSnapshotRef.current.set(sn, evt.state);
      else holdSnapshotRef.current.delete(sn);
    }
    // Reset throttle
    lastSentRef.delete(sn);
  }, [lastSentRef, scheduleFor]);

  const releaseManual = useCallback((sn: string) => {
    if (!sn) return;
    manualHoldRef.current.delete(sn);
    holdSnapshotRef.current.delete(sn);
    lastSentRef.delete(sn);
  }, [lastSentRef]);

  const getManualHold = useCallback(
    (sn: string): 0 | 1 | null => (manualHoldRef.current.has(sn) ? manualHoldRef.current.get(sn)! : null),
    [],
  );

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      const now = Date.now();
      const nowDate = new Date();
      for (const dev of devices) {
        if (!dev.device_sn) continue;
        const sch = scheduleFor(dev.device_sn);
        if (!sch || !sch.enabled) continue;
        const evt = computeNextEvent(sch, nowDate);
        if (!evt) continue;
        const expected = evt.state;

        // ── Override manuel : on respecte le hold tant que la planification
        //    n'a pas naturellement changé d'état depuis le clic.
        if (manualHoldRef.current.has(dev.device_sn)) {
          const snap = holdSnapshotRef.current.get(dev.device_sn);
          if (snap !== undefined && expected === snap) {
            // L'état planifié est toujours le même qu'au moment du clic → hold actif
            continue;
          }
          // L'état planifié a changé → reprise auto, on libère
          manualHoldRef.current.delete(dev.device_sn);
          holdSnapshotRef.current.delete(dev.device_sn);
        }

        // Si l'état actuel du device est inconnu, on n'envoie rien
        if (dev.relay_state === null || dev.relay_state === undefined) continue;
        if (dev.relay_state === expected) continue;
        // Throttling
        const last = lastSentRef.get(dev.device_sn);
        if (last && last.value === expected && now - last.at < cooldownMs) continue;
        // Envoi !
        try {
          // eslint-disable-next-line no-console
          console.log(`[Schedule] ${dev.device_sn} : état réel=${dev.relay_state} → attendu=${expected}, envoi commande...`);
          // source='schedule' : le serveur accepte la commande quel que soit
          // le rôle de l'utilisateur connecté, s'il retrouve cet état dans le
          // planning actif en base.
          Promise.resolve(sendRelay(dev.device_sn, expected, { source: 'schedule' })).catch(() => undefined);
          lastSentRef.set(dev.device_sn, { value: expected, at: now });
        } catch {
          /* ignore */
        }
      }
    };

    tick(); // immédiat
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [devices, sendRelay, intervalMs, cooldownMs, enabled, lastSentRef, scheduleFor]);

  return { forceManual, releaseManual, getManualHold };
}

